(() => {
  const instances = new WeakMap();
  const BASE_ITEM_CLASSES =
    "absolute inset-x-0 inset-y-0 flex items-center text-xs tracking-wide leading-none transition-all duration-500 ease-out";

  class PrototypeAnnouncementBar {
    constructor(element) {
      this.element = element;
      this.items = [];
      this.currentIndex = 0;
      this.intervalId = null;
      this.isDestroyed = false;
      this.handleScroll = this.handleScroll.bind(this);

      this.init();
    }

    async init() {
      await this.insertReviewsAnnouncement();
      this.refreshItems();

      if (!this.items.length || this.isDestroyed) return;

      this.applyState();
      this.element.dataset.ready = "true";
      this.handleScroll();
      window.addEventListener("scroll", this.handleScroll, { passive: true });
      this.startRotation();
    }

    refreshItems() {
      this.items = Array.from(
        this.element.querySelectorAll("[data-announcement-item]"),
      );
    }

    startRotation() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
      }

      if (this.items.length <= 1) return;

      const rotateInterval = Number.parseInt(
        this.element.dataset.rotateInterval || "0",
        10,
      );

      if (rotateInterval > 0) {
        this.intervalId = window.setInterval(() => {
          this.currentIndex = (this.currentIndex + 1) % this.items.length;
          this.applyState();
        }, rotateInterval);
      }
    }

    async insertReviewsAnnouncement() {
      const reviewsUrl = this.element.dataset.reviewsUrl;
      const reviewsTimeout = Number.parseInt(
        this.element.dataset.reviewsTimeout || "5000",
        10,
      );

      if (!reviewsUrl) return;

      try {
        const response = await Promise.race([
          window.fetch(reviewsUrl, {
            headers: { Accept: "application/json" },
            cache: "force-cache",
          }),
          new Promise((_, reject) => {
            window.setTimeout(() => {
              reject(new Error("Reviews request timed out"));
            }, reviewsTimeout);
          }),
        ]);

        if (!response.ok) return;

        const payload = await response.json();
        const reviewAnnouncement = this.buildReviewsAnnouncement(payload);

        if (!reviewAnnouncement || this.isDestroyed) return;

        const container = this.element.querySelector(".relative.h-full.w-full");

        if (!container) return;

        container.prepend(reviewAnnouncement);
      } catch (_error) {
        // Fall back to the static announcements when Reviews.io is unavailable.
      }
    }

    buildReviewsAnnouncement(payload) {
      const verdict = payload?.verdict;
      const reviewCount = Number.parseInt(payload?.review_count, 10);
      const averageRating = Number.parseFloat(payload?.average_rating);

      if (
        !verdict ||
        !Number.isFinite(reviewCount) ||
        !Number.isFinite(averageRating)
      ) {
        return null;
      }

      const roundedRating = Math.ceil(averageRating * 100) / 100;
      const displayRating = roundedRating.toFixed(2);
      const starCount = Math.max(1, Math.min(5, Math.ceil(roundedRating)));
      const starMarkup = "&starf;".repeat(starCount);
      const formattedReviewCount = new Intl.NumberFormat("en-GB").format(
        reviewCount,
      );

      const item = document.createElement("div");
      item.className = BASE_ITEM_CLASSES;
      item.dataset.announcementItem = "";
      item.setAttribute(
        "aria-label",
        `${verdict}. ${displayRating} based on ${formattedReviewCount} reviews`,
      );
      item.innerHTML = `<span><span class="text-accent">${starMarkup}</span> (${displayRating}) based on ${formattedReviewCount} reviews</span>`;

      return item;
    }

    applyState() {
      if (!this.items.length) return;

      const previousIndex =
        (this.currentIndex - 1 + this.items.length) % this.items.length;

      this.items.forEach((item, index) => {
        item.classList.remove(
          "translate-y-0",
          "-translate-y-full",
          "translate-y-full",
          "opacity-100",
          "opacity-0",
          "pointer-events-auto",
          "pointer-events-none",
        );

        if (index === this.currentIndex) {
          item.classList.add(
            "translate-y-0",
            "opacity-100",
            "pointer-events-auto",
          );
          return;
        }

        if (index === previousIndex) {
          item.classList.add(
            "-translate-y-full",
            "opacity-0",
            "pointer-events-none",
          );
          return;
        }

        item.classList.add(
          "translate-y-full",
          "opacity-0",
          "pointer-events-none",
        );
      });
    }

    handleScroll() {
      this.element.dataset.hidden = window.scrollY >= 10 ? "true" : "false";
    }

    destroy() {
      this.isDestroyed = true;

      if (this.intervalId) {
        window.clearInterval(this.intervalId);
      }

      window.removeEventListener("scroll", this.handleScroll);
    }
  }

  function initAnnouncementBars(root = document) {
    root.querySelectorAll("[data-announcement-bar]").forEach((element) => {
      if (instances.has(element)) return;
      instances.set(element, new PrototypeAnnouncementBar(element));
    });
  }

  document.addEventListener("DOMContentLoaded", () => initAnnouncementBars());
  document.addEventListener("shopify:section:load", (event) =>
    initAnnouncementBars(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    event.target
      .querySelectorAll("[data-announcement-bar]")
      .forEach((element) => {
        const instance = instances.get(element);
        if (!instance) return;
        instance.destroy();
        instances.delete(element);
      });
  });
})();
